import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BaseCard from '../../common/layout/BaseCard';
import { Button } from '../../common/ui/Button';
import { ModalDialog } from '../../common/ui/ModalDialog';
import { Dropdown } from '../../common/ui/Listbox';

import Tooltip from '../../common/ui/Tooltip';
import { DataTable, DataTableColumn, DataTablePagination, DataTableSelection, DataTableSort } from '../../common/display/DataTable';
import Avatar from '../../common/display/Avatar';
import Pill from '../../common/display/Pill';
import { supabase } from '../../../lib/supabase';
import { captureAppException } from '../../../utils/errorCaptureUtils'; // Note: Add createSupabaseError back if needed
import { useAuth } from '../../../lib/AuthContext';
import { CheckIcon } from '@heroicons/react/24/outline';

interface DisplayMember {
  workspace_user_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'member';
  seat_type: string;
  joined_at: string;
  type: 'member' | 'invite';
  users: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_image_url: string | null;
  } | null;
  // For invites
  email?: string;
  expires_at?: string;
  invited_by?: string;
  inviter?: {
    first_name: string;
    last_name: string;
  };
}

interface ConfirmActionState {
  memberIds: Set<string>;
  action: 'remove' | 'change_role' | 'change_seat_type' | 'leave';
  memberName?: string;
  currentRole?: string;
  newRole?: string;
  currentSeatType?: string;
  newSeatType?: string;
}

interface BulkAction {
  type: 'change_role' | 'change_seat_type' | 'remove_members' | null;
  roleChange?: string;
  seatTypeChange?: string;
  confirmModal?: ConfirmActionState;
}

interface TableState {
  currentPage: number;
  itemsPerPage: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  selectedItems: Set<string>;
}

interface WorkspaceConstraints {
  totalOwners: number;
  isLastOwner: boolean;
  currentUserRole: 'owner' | 'editor' | 'member' | null;
  canManageUsers: boolean;
  canManageOwners: boolean;
}

const ROLE_HIERARCHY = { owner: 3, editor: 2, member: 1 };
const ROLE_OPTIONS = [
  { id: 'member', text: 'Member' },
  { id: 'editor', text: 'Editor' },
  { id: 'owner', text: 'Owner' }
];
const BULK_ACTIONS = [
  { id: 'change_role', text: 'Change Role' },
  { id: 'change_seat_type', text: 'Toggle Pro Seat' },
  { id: 'remove_members', text: 'Remove Members' }
];

export const ManageWorkspaceMembers = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState<DisplayMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [confirmingAction, setConfirmingAction] = useState<ConfirmActionState | null>(null);
  const [workspace, setWorkspace] = useState<{
    total_paid_seats_current: number | null;
    is_founder: boolean;
  } | null>(null);
  const [proSeatsInUse, setProSeatsInUse] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  const [tableState, setTableState] = useState<TableState>({
    currentPage: 1,
    itemsPerPage: 10,
    sortField: 'name',
    sortDirection: 'asc',
    selectedItems: new Set()
  });

  const [bulkAction, setBulkAction] = useState<BulkAction>({ type: null });

  // Utility functions
  const getUserFullName = (member: DisplayMember) => {
    if (member.type === 'invite') return member.email || 'Unknown';
    const firstName = member.users?.first_name || '';
    const lastName = member.users?.last_name || '';
    return `${lastName}, ${firstName}`.trim();
  };

  const getMemberStatus = (member: DisplayMember) => 
    member.type === 'invite' 
      ? { text: 'Invite pending', className: 'bg-yellow-100 text-yellow-800 text-xs' }
      : { text: 'Active', className: 'bg-green-100 text-green-800 text-xs' };

  const isRoleDowngrade = (currentRole: string, newRole: string) => 
    ROLE_HIERARCHY[newRole as keyof typeof ROLE_HIERARCHY] < ROLE_HIERARCHY[currentRole as keyof typeof ROLE_HIERARCHY];

  // Workspace constraints
  const workspaceConstraints = useMemo((): WorkspaceConstraints => {
    const actualMembers = members.filter(m => m.type === 'member');
    const owners = actualMembers.filter(m => m.role === 'owner');
    const currentUserMember = actualMembers.find(m => m.user_id === user?.id);
    const currentUserRole = currentUserMember?.role || null;
    const isLastOwner = owners.length === 1 && currentUserRole === 'owner';
    
    return {
      totalOwners: owners.length,
      isLastOwner,
      currentUserRole,
      canManageUsers: currentUserRole === 'owner' || currentUserRole === 'editor',
      canManageOwners: currentUserRole === 'owner'
    };
  }, [members, user?.id]);

  // Permission checks
  const canModifyMember = (member: DisplayMember) => {
    const isCurrentUser = member.user_id === user?.id;
    if (!workspaceConstraints.canManageUsers) return false;
    if (member.role === 'owner' && !workspaceConstraints.canManageOwners) return false;
    if (isCurrentUser && workspaceConstraints.isLastOwner) return false;
    return true;
  };

  const getActionRestriction = (member: DisplayMember, action: string) => {
    const isCurrentUser = member.user_id === user?.id;
    
    if (action === 'leave' && isCurrentUser && workspaceConstraints.isLastOwner) {
      return 'You cannot leave as you are the last owner. Transfer ownership or add another owner first.';
    }
    if (member.role === 'owner' && !workspaceConstraints.canManageOwners) {
      return 'Only workspace owners can manage other owners.';
    }
    if (!workspaceConstraints.canManageUsers) {
      return 'You do not have permission to manage workspace members.';
    }
    if (isCurrentUser && workspaceConstraints.isLastOwner && action !== 'leave') {
      return 'Cannot remove the last owner from the workspace.';
    }
    return null;
  };

  const getBulkActionRestriction = (action: string, newRole?: string) => {
    if (!workspaceConstraints.canManageUsers) {
      return 'You do not have permission to manage workspace members.';
    }
    
    const selectedMembers = members.filter(m => tableState.selectedItems.has(m.workspace_user_id));
    const selectedOwners = selectedMembers.filter(m => m.role === 'owner');
    const currentUserSelected = selectedMembers.find(m => m.user_id === user?.id);
    
    if (selectedOwners.length > 0 && !workspaceConstraints.canManageOwners) {
      return `You cannot ${action} workspace owners. Only workspace owners can manage other owners.`;
    }
    
    // Check if trying to remove the last owner (including self)
    if (action === 'remove' && workspaceConstraints.isLastOwner && currentUserSelected) {
      return 'You cannot remove yourself as you are the last owner. Transfer ownership or add another owner first.';
    }
    
    // Check for role downgrade restrictions
    if (action === 'change roles of' && newRole && currentUserSelected) {
      if (isRoleDowngrade(currentUserSelected.role, newRole)) {
        return `You cannot downgrade your own role from ${currentUserSelected.role} to ${newRole} in bulk. Please use individual action instead.`;
      }
    }
    
    // Check pro seat availability for bulk toggle
    if (action === 'toggle pro seats') {
      const membersToToggleToPro = selectedMembers.filter(m => m.seat_type !== 'pro' && m.type === 'member');
      const availableProSeats = workspace?.is_founder ? Infinity : (workspace?.total_paid_seats_current || 0) - proSeatsInUse;
      
      if (membersToToggleToPro.length > availableProSeats) {
        return `Not enough pro seats available. You need ${membersToToggleToPro.length} pro seats but only have ${availableProSeats} available.`;
      }
    }
    
    return null;
  };

  const getAvailableRoles = () => 
    workspaceConstraints.canManageOwners 
      ? ROLE_OPTIONS 
      : ROLE_OPTIONS.filter(role => role.id !== 'owner');

  // Window resize handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data fetching
  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const [workspaceResult, membersResult] = await Promise.all([
        supabase.from('workspaces').select('total_paid_seats_current, is_founder').eq('workspace_id', id).single(),
        supabase.from('workspace_users').select('*, users (first_name, last_name, email, profile_image_url)').eq('workspace_id', id).order('joined_at', { ascending: false })
      ]);

      if (workspaceResult.error) throw workspaceResult.error;
      if (membersResult.error) throw membersResult.error;
      setWorkspace(workspaceResult.data);
      setMembers((membersResult.data || []).map(member => ({
        ...member,
        type: 'member' as const
      })));
      setProSeatsInUse(membersResult.data?.filter(m => m.seat_type === 'pro').length || 0);
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_workspace_members', workspace_id: id, action: 'fetch_data' } });
      setError('Failed to fetch workspace data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [id]);

  useEffect(() => {
    setError(null);
  }, [tableState.selectedItems, bulkAction.type]);

  // Generic update function
  const updateMemberData = async (userId: string, table: string, updates: any, successCallback?: () => void) => {
    setUpdating(userId);
    try {
      const member = members.find(m => m.workspace_user_id === userId);
      if (!member) throw new Error('Member not found');

      const query = member.type === 'invite' 
        ? supabase.from('workspace_invites').update(updates).eq('id', userId).eq('workspace_id', id)
        : supabase.from('workspace_users').update(updates).eq('workspace_id', id).eq('user_id', member.user_id);

      const { error } = await query;
      if (error) throw error;

      setMembers(members => members.map(m => 
        m.workspace_user_id === userId ? { ...m, ...updates } : m
      ));
      
      successCallback?.();
      setConfirmingAction(null);
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_workspace_members', workspace_id: id, user_id: userId, action: table } });
      setError(`Failed to update ${table}.`);
    } finally {
      setUpdating(null);
    }
  };

  // Event handlers
  const updateMemberRole = (userId: string, newRole: string) => 
    updateMemberData(userId, 'role', { role: newRole });

  const toggleProSeat = async (userId: string, currentSeatType: string) => {
    const newSeatType = currentSeatType === 'pro' ? 'free' : 'pro';
    
    if (newSeatType === 'pro' && !workspace?.is_founder && proSeatsInUse >= (workspace?.total_paid_seats_current || 0)) {
      setError('No pro seats available.');
      return;
    }

    await updateMemberData(userId, 'seat_type', { seat_type: newSeatType }, () => {
      const member = members.find(m => m.workspace_user_id === userId);
      if (member?.type !== 'invite') {
        setProSeatsInUse(prev => newSeatType === 'pro' ? prev + 1 : prev - 1);
      }
    });
  };

  const removeMember = async (userId: string) => {
    setUpdating(userId);
    try {
      const member = members.find(m => m.workspace_user_id === userId);
      if (!member) throw new Error('Member not found');

      const query = member.type === 'invite' 
        ? supabase.from('workspace_invites').delete().eq('id', userId)
        : supabase.from('workspace_users').delete().eq('workspace_id', id).eq('user_id', member.user_id);

      const { error } = await query;
      if (error) throw error;

      setMembers(members => members.filter(m => m.workspace_user_id !== userId));
      setConfirmingAction(null);
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_workspace_members', workspace_id: id, user_id: userId, action: 'remove_member' } });
      setError('Failed to remove member.');
    } finally {
      setUpdating(null);
    }
  };

  const leaveWorkspace = async () => {
    if (!user) return;
    setUpdating(user.id);
    try {
      const { error } = await supabase.from('workspace_users').delete().eq('workspace_id', id).eq('user_id', user.id);
      if (error) throw error;
      navigate('/workspaces');
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_workspace_members', workspace_id: id, user_id: user.id, action: 'leave_workspace' } });
      setError('Failed to leave workspace.');
    } finally {
      setUpdating(null);
      setConfirmingAction(null);
    }
  };

  // Bulk actions
  const handleBulkActionChange = (actionId: string | null) => {
    setBulkAction({ type: actionId as BulkAction['type'] });
  };

  const handleSecondaryOptionChange = (optionId: string) => {
    setBulkAction({ ...bulkAction, roleChange: optionId });
  };

  const handleBulkActionExecute = (actionId: string, selectedItems: Set<string>) => {
    let restriction: string | null = null;
    
    if (actionId === 'remove_members') {
      restriction = getBulkActionRestriction('remove');
    } else if (actionId === 'change_role') {
      restriction = getBulkActionRestriction('change roles of', bulkAction.roleChange);
    } else if (actionId === 'change_seat_type') {
      restriction = getBulkActionRestriction('toggle pro seats');
    }
    
    if (restriction) {
      setError(restriction);
      return;
    }
    
    setConfirmingAction({
      memberIds: selectedItems,
      action: actionId === 'remove_members' ? 'remove' : 
              actionId === 'change_role' ? 'change_role' : 'change_seat_type',
      newRole: bulkAction.roleChange
    });
  };

  const confirmBulkAction = async () => {
    if (!confirmingAction) return;
    
    try {
      const memberIds = Array.from(confirmingAction.memberIds);
      
      if (confirmingAction.action === 'change_role' && confirmingAction.newRole) {
        await Promise.all(memberIds.map(id => updateMemberRole(id, confirmingAction.newRole!)));
      } else if (confirmingAction.action === 'change_seat_type') {
        await Promise.all(memberIds.map(id => {
          const member = members.find(m => m.workspace_user_id === id);
          return toggleProSeat(id, member?.seat_type || 'free');
        }));
      } else if (confirmingAction.action === 'remove') {
        await Promise.all(memberIds.map(id => removeMember(id)));
      }
      
      setTableState(prev => ({ ...prev, selectedItems: new Set() }));
      setBulkAction({ type: null });
      await fetchMembers();
    } catch (err) {
      captureAppException(err);
      setError('Failed to perform bulk action');
    }
  };

  // Data processing
  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;
    const searchStr = searchTerm.toLowerCase();
    return members.filter(member => {
      const searchableText = [
        getUserFullName(member),
        member.users?.email || member.email || '',
        member.role
      ].join(' ').toLowerCase();
      return searchableText.includes(searchStr);
    });
  }, [members, searchTerm]);

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      let compareValue = 0;
      const { sortField, sortDirection } = tableState;
      
      switch (sortField) {
        case 'name':
          compareValue = getUserFullName(a).localeCompare(getUserFullName(b));
          break;
        case 'role':
          compareValue = a.role.localeCompare(b.role);
          break;
        case 'seat_type':
          compareValue = (a.seat_type || 'free').localeCompare(b.seat_type || 'free');
          break;
        case 'joined_at':
          compareValue = new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
          break;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }, [filteredMembers, tableState.sortField, tableState.sortDirection]);

  const paginatedMembers = useMemo(() => {
    const { currentPage, itemsPerPage } = tableState;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedMembers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedMembers, tableState.currentPage, tableState.itemsPerPage]);

  // Table handlers
  const updateTableState = (updates: Partial<TableState>) => {
    setTableState(prev => ({ ...prev, ...updates }));
  };

  const handleSort = (field: string) => {
    updateTableState({
      sortField: field,
      sortDirection: tableState.sortField === field && tableState.sortDirection === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const currentPageIds = paginatedMembers.map(member => member.workspace_user_id);
    const allCurrentPageSelected = currentPageIds.every(id => tableState.selectedItems.has(id));
    const hasSelectionsAcrossPages = tableState.selectedItems.size > 0 && 
      Array.from(tableState.selectedItems).some(id => !currentPageIds.includes(id));
    
    if (!checked && hasSelectionsAcrossPages) {
      // If unchecking and there are selections across multiple pages, deselect ALL
      updateTableState({ selectedItems: new Set() });
    } else if (checked && allCurrentPageSelected && members.length > paginatedMembers.length) {
      // If all current page items are already selected and user clicks "Select All",
      // select ALL members across all pages
      const allMemberIds = members.map(member => member.workspace_user_id);
      updateTableState({ selectedItems: new Set(allMemberIds) });
    } else {
      // Normal behavior: select/deselect current page items
      const newSelected = new Set(tableState.selectedItems);
      currentPageIds.forEach(id => checked ? newSelected.add(id) : newSelected.delete(id));
      updateTableState({ selectedItems: newSelected });
    }
  };

  const handleSelectOne = (memberId: string, checked: boolean) => {
    const newSelected = new Set(tableState.selectedItems);
    checked ? newSelected.add(memberId) : newSelected.delete(memberId);
    updateTableState({ selectedItems: newSelected });
  };

  // Render helpers
  const renderMemberCell = (member: DisplayMember) => (
    <div className="flex items-center gap-3">
      <Avatar
        firstName={member.users?.first_name || ''}
        lastName={member.users?.last_name || ''}
        profileImageUrl={member.users?.profile_image_url || undefined}
        size="small"
      />
      <div className="min-w-0">
        <p className="font-medium text-gray-900 truncate">{getUserFullName(member)}</p>
        <p className="text-xs text-gray-500 truncate">{member.users?.email || member.email}</p>
      </div>
    </div>
  );

  const renderMemberMobile = (member: DisplayMember) => (
    <div className="flex items-start gap-3 flex-1">
      <Avatar
        firstName={member.users?.first_name || ''}
        lastName={member.users?.last_name || ''}
        profileImageUrl={member.users?.profile_image_url || undefined}
        size="medium"
        className="shrink-0"
      />
      <div>
        <p className="font-medium text-gray-900">{getUserFullName(member)}</p>
        <p className="text-sm text-gray-500">{member.users?.email || member.email}</p>
        <div className="mt-1">
          {(() => {
            const status = getMemberStatus(member);
            return <Pill text={status.text} className={status.className} />;
          })()}
        </div>
      </div>
    </div>
  );

  const renderRoleCell = (member: DisplayMember) => {
    const isCurrentUser = member.user_id === user?.id;
    const canModify = canModifyMember(member);
    const restrictionReason = getActionRestriction(member, 'role_change');
    
    const roleDropdown = (
      <Dropdown
        options={getAvailableRoles()}
        value={member.role}
        onChange={(option) => {
          if (isCurrentUser && isRoleDowngrade(member.role, option.id)) {
            setConfirmingAction({
              memberIds: new Set([member.workspace_user_id]),
              action: 'change_role',
              memberName: getUserFullName(member),
              currentRole: member.role,
              newRole: option.id
            });
          } else {
            updateMemberRole(member.workspace_user_id, option.id);
          }
        }}
        disabled={updating === member.workspace_user_id || !canModify}
        size="small"
        className="w-24 lg:w-30"
      />
    );

    return restrictionReason ? (
      <Tooltip text={restrictionReason}>{roleDropdown}</Tooltip>
    ) : roleDropdown;
  };

  const renderProSeatCell = (member: DisplayMember) => {
    const canAssignProSeat = workspace?.is_founder || proSeatsInUse < (workspace?.total_paid_seats_current || 0);
    const isProSeatDisabled = updating === member.workspace_user_id || (!canAssignProSeat && member.seat_type !== 'pro');
    
    const proSeatButton = (
      <button
        onClick={() => toggleProSeat(member.workspace_user_id, member.seat_type || 'free')}
        disabled={isProSeatDisabled}
        className={`w-4 h-4 border rounded flex items-center justify-center ${
          member.seat_type === 'pro' ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
        } ${isProSeatDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-600'}`}
      >
        {member.seat_type === 'pro' && (
          <CheckIcon className="w-3 h-3 text-white" />
        )}
      </button>
    );

    return (
      <div className="flex justify-center">
        {isProSeatDisabled && !workspace?.is_founder ? (
          <Tooltip text="No more pro seats available. Please purchase more seats or unassign some existing ones.">
            {proSeatButton}
          </Tooltip>
        ) : (
          proSeatButton
        )}
      </div>
    );
  };

  const renderActionsCell = (member: DisplayMember) => {
    const isCurrentUser = member.user_id === user?.id;
    const canModify = canModifyMember(member);
    const canLeave = isCurrentUser && !workspaceConstraints.isLastOwner;
    const restrictionReason = getActionRestriction(member, isCurrentUser ? 'leave' : 'remove');
    
    const actionButton = (
      <Button
        variant={isCurrentUser ? "filled" : "outline"}
        size="small"
        color="danger"
        onClick={() => {
          if (isCurrentUser) {
            setConfirmingAction({
              memberIds: new Set([member.workspace_user_id]),
              action: 'leave',
              memberName: getUserFullName(member)
            });
          } else {
            removeMember(member.workspace_user_id);
          }
        }}
        disabled={updating === member.workspace_user_id || (isCurrentUser ? !canLeave : !canModify)}
      >
        {isCurrentUser ? 'Leave' : member.type === 'invite' ? 'Cancel' : 'Remove'}
      </Button>
    );

    return restrictionReason ? (
      <Tooltip text={restrictionReason}>{actionButton}</Tooltip>
    ) : actionButton;
  };

  // Column definitions
  const createRenderFunction = (content: (member: DisplayMember) => React.ReactNode) => (member: DisplayMember) => content(member);
  
  const allColumns: DataTableColumn<DisplayMember>[] = [
    {
      key: 'name',
      label: 'Member',
      sortable: true,
      width: 'minmax(250px,2fr)',
      render: createRenderFunction(renderMemberCell),
      mobileRender: createRenderFunction(renderMemberMobile)
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      width: 'minmax(120px,1fr)',
      render: createRenderFunction(renderRoleCell),
      mobileLabel: 'Role',
      mobileRender: createRenderFunction(member => <span className="text-sm font-medium capitalize">{member.role}</span>)
    },
    {
      key: 'seat_type',
      label: 'Pro Seat',
      sortable: true,
      width: 'minmax(100px,1fr)',
      align: 'center',
      render: createRenderFunction(renderProSeatCell),
      mobileLabel: 'Pro Seat',
      mobileRender: createRenderFunction(member => <span className="text-sm">{member.seat_type === 'pro' ? 'Yes' : 'No'}</span>)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: 'minmax(120px,1fr)',
      render: createRenderFunction(member => {
        const status = getMemberStatus(member);
        return <Pill text={status.text} className={status.className} />;
      }),
      mobileLabel: 'Status'
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 'minmax(100px,1fr)',
      align: 'center',
      render: createRenderFunction(renderActionsCell)
    }
  ];

  // Responsive columns and grid
  const columns = useMemo(() => {
    const isLargeScreen = windowWidth >= 1024;
    return isLargeScreen ? allColumns : allColumns.filter(col => col.key !== 'seat_type');
  }, [allColumns, windowWidth]);

  const gridCols = useMemo(() => {
    const isLargeScreen = windowWidth >= 1024;
    return isLargeScreen 
      ? "grid-cols-[48px_minmax(250px,2fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)]"
      : "grid-cols-[48px_minmax(250px,2fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)]";
  }, [windowWidth]);

  // Computed values
  const totalItems = filteredMembers.length;
  const totalPages = Math.ceil(totalItems / tableState.itemsPerPage);
  const isAllCurrentPageSelected = paginatedMembers.length > 0 && 
    paginatedMembers.every(member => tableState.selectedItems.has(member.workspace_user_id));
  const isIndeterminate = paginatedMembers.length > 0 && 
    paginatedMembers.some(member => tableState.selectedItems.has(member.workspace_user_id)) && 
    !isAllCurrentPageSelected;

  // Table configuration
  const pagination: DataTablePagination = {
    currentPage: tableState.currentPage,
    totalPages,
    totalItems,
    itemsPerPage: tableState.itemsPerPage,
    onPageChange: (page) => updateTableState({ currentPage: page }),
    onItemsPerPageChange: (size) => updateTableState({ itemsPerPage: size, currentPage: 1 })
  };

  const selection: DataTableSelection<DisplayMember> = {
    selectedItems: tableState.selectedItems,
    onSelectAll: handleSelectAll,
    onSelectOne: handleSelectOne,
    getItemId: (member) => member.workspace_user_id,
    isSelectAll: isAllCurrentPageSelected,
    isIndeterminate
  };

  const sort: DataTableSort = {
    field: tableState.sortField,
    direction: tableState.sortDirection,
    onSort: handleSort
  };

  if (loading) {
    return (
      <BaseCard>
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-base"></div>
        </div>
      </BaseCard>
    );
  }

  return (
    <BaseCard>
      <div className="space-y-4 -mx-7 md:mx-0">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 py-4 border-b border-gray-200 px-6 md:px-0">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">Workspace Members</h2>
          </div>
          <div className="text-sm text-gray-600">
            {workspace?.is_founder ? (
              <span>{proSeatsInUse} / ∞ pro seats</span>
            ) : (
              <span>{proSeatsInUse} / {workspace?.total_paid_seats_current || 0} pro seats</span>
            )}
          </div>
        </div>





        {error && (
          <div className="px-5 md:px-0">
            <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
          </div>
        )}

        <DataTable
          columns={columns}
          data={paginatedMembers}
          loading={loading}
          error={error}
          emptyMessage={searchTerm ? 'No members match your search.' : 'No members found.'}
          pagination={pagination}
          selection={selection}
          sort={sort}
          search={{
            value: searchTerm,
            onChange: (value: string) => {
              setSearchTerm(value || '');
              updateTableState({ currentPage: 1 });
            },
            placeholder: "Search members...",
            className: "w-full sm:w-64"
          }}
          bulkActions={{
            actions: BULK_ACTIONS,
            selectedAction: bulkAction.type,
            onActionChange: handleBulkActionChange,
            onActionExecute: handleBulkActionExecute,
            
            // Secondary options for role changes
            secondaryOptions: getAvailableRoles(),
            selectedSecondaryOption: bulkAction.roleChange,
            onSecondaryOptionChange: handleSecondaryOptionChange,
            
            // Permission and restriction handling
            canPerformActions: workspaceConstraints.canManageUsers,
            restrictionMessage: !workspaceConstraints.canManageUsers ? 'You do not have permission to manage workspace members.' : undefined,
            
            // Actions that require secondary selection
            requiresSecondarySelection: new Set(['change_role']),
            
            // Custom action buttons
            customActionButtons: {
              'change_seat_type': {
                text: 'Toggle Pro Seats',
                variant: 'filled'
              },
              'remove_members': {
                text: 'Remove Selected',
                variant: 'filled',
                color: 'danger'
              }
            }
          }}
          gridCols={gridCols}
          totalItemsCount={members.length}
        />

        <ModalDialog
          isOpen={!!confirmingAction}
          onClose={() => setConfirmingAction(null)}
          onPrimaryButtonClick={() => {
            if (!confirmingAction) return;
            
            if (confirmingAction.action === 'leave') {
              leaveWorkspace();
            } else if (confirmingAction.action === 'change_role' && confirmingAction.newRole) {
              if (confirmingAction.memberIds.size === 1) {
                const memberId = Array.from(confirmingAction.memberIds)[0];
                updateMemberRole(memberId, confirmingAction.newRole);
              } else {
                confirmBulkAction();
              }
            } else {
              confirmBulkAction();
            }
          }}
          onSecondaryButtonClick={() => setConfirmingAction(null)}
          title={
            !confirmingAction ? '' :
            confirmingAction.action === 'leave' ? 'Leave Workspace' : 
            confirmingAction.action === 'change_role' ? 'Change Role' :
            confirmingAction.action === 'change_seat_type' ? 'Toggle Pro Seats' : 'Remove Members'
          }
          description={
            !confirmingAction ? '' :
            confirmingAction.action === 'leave'
              ? 'Are you sure you want to leave this workspace? You will need to be invited back to rejoin.'
              : confirmingAction.action === 'change_role' && confirmingAction.currentRole && confirmingAction.newRole
              ? `Are you sure you want to downgrade your role from ${confirmingAction.currentRole} to ${confirmingAction.newRole}?`
              : confirmingAction.action === 'change_role'
              ? `This will change the role of ${confirmingAction.memberIds.size} member(s) to "${confirmingAction.newRole}".`
              : confirmingAction.action === 'change_seat_type'
              ? `This will toggle pro seat assignments for ${confirmingAction.memberIds.size} member(s).`
              : `This will remove ${confirmingAction.memberIds.size} member(s) from the workspace.`
          }
          primaryButtonText={confirmingAction?.action === 'leave' ? 'Leave Workspace' : 'Confirm'}
          icon={confirmingAction?.action === 'leave' || confirmingAction?.action === 'remove' ? 'warning' : 'info'}
          primaryButtonColor={confirmingAction?.action === 'leave' || confirmingAction?.action === 'remove' ? 'danger' : 'primary'}
        />
      </div>
    </BaseCard>
  );
};
